import React, { useEffect, useMemo, useState } from "react";
import { get } from "lodash";
import { IconLogout } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  Navbar,
  ScrollArea,
  AppShell,
  Stack,
  Button,
  Text,
  ActionIcon,
  Box,
  Image,
  Flex,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useLocation } from "react-router-dom";
import {
  LinksNormal,
  LinkLite,
} from "components/layout/nav-bar-links-group/NavBarLinksGroup";
import { USER_ROLE_KHDN, USER_TYPE } from "constants/enum";
import RoutesString from "pages/routesString";
import useAuthentication from "stores/AuthenticationStore/authentication";
import useConfiguration from "stores/ConfigurationStore/configuration";
import ConfigurationLoader from "components/common/configuration-loader/ConfigurationLoader";
import "./LoggedInLayout.scss";

import { menuForChecker, menuForMaker, menuForMerchant } from "./MenuConfig";
import DrawerUserInfo from "components/layout/drawer-user-info/DrawerUserInfo";
import NotificationPopup from "components/modal/popup/notifications-popup/NotificationPopup";
import HeaderBar from "../header-bar/HeaderBar";
import { isMobile } from "react-device-detect";
import ModalSetting from "components/modal/modalSetting/ModalSetting";
import notiLogout from "assets/images/pngs/noti-logout.png";
import { useGetCustomerAccountQuery } from "services/accountAPI";
import useAuth from "@/hooks/useAuth";
import ImgJavis from "@/assets/images/pngs/img-javis.png";
import IconCollapseLeft from "@/assets/images/svgs/icon-collapes-left.svg";
import IconCollapseRight from "@/assets/images/svgs/icon-collapse-right.svg";

type LoggedLayoutProps = {
  children: React.ReactNode;
};

const LoggedLayout = ({ children }: LoggedLayoutProps) => {
  const [sideBar, setSideBar] = useState({ collapsed: !isMobile });
  const location = useLocation();
  const { t, i18n } = useTranslation(); // Get i18n instance to track language changes
  const [openedNoti, { open: openNoti, close: closeNoti }] =
    useDisclosure(false);
  const [stateConfig] = useConfiguration();
  const [titlePopup, setTitlePopup] = useState("");
  const [descriptionPopup, setDescriptionPopup] = useState("");
  const [stateAuth, actionAuth] = useAuthentication();
  const [openedDrawer, { open, close }] = useDisclosure(false);
  const [menu, setMenu] = useState<any>([]);
  const [openLogout, setOpenLogout] = useState(false);
  const [openedMenu, setOpenedMenu] = useState<string | null>(null);
  const userType = get(stateAuth, ["user", "userType"], "");
  const userRole = get(stateAuth, ["user", "userRole"], "");
  const userRoleList = [
    USER_ROLE_KHDN.ADMIN,
    USER_ROLE_KHDN.CHECKER,
    USER_ROLE_KHDN.MAKER,
  ];

  const { data, error, isLoading, isError } = useGetCustomerAccountQuery();
  const { user } = useAuth();
  const role = useMemo(() => get(user, "userRole", ""), [user]);
  // Prefer getUserProfile API response; fall back to localStorage set at login time
  const orgType =
    (user as any)?.orgType ||
    localStorage.getItem("orgType") ||
    "";
  const isMerchant = orgType === "MERCHANT";
  const toggleSideBar = (collapsed: boolean) => {
    setSideBar((prev) => ({ ...prev, collapsed }));
  };

  const userTypeList = [USER_TYPE.KHCN, USER_TYPE.KHDN];

  const links = menu.map((item: any) => (
    <LinksNormal
      {...item}
      key={item.id}
      openedMenu={openedMenu}
      setOpenedMenu={setOpenedMenu}
    />
  ));

  const linksLite = menu.map((link: any) => (
    <LinkLite {...link} key={link.id} toggleSideBar={toggleSideBar} />
  ));

  const setMenuByUserRole = (userRoleValue: string) => {
    if (isMerchant) return menuForMerchant(location.pathname, t);
    switch (userRoleValue) {
      case USER_ROLE_KHDN.MAKER:
        return menuForMaker(location.pathname, t);
      case USER_ROLE_KHDN.CHECKER:
        return menuForChecker(location.pathname, t);
      default:
        return menuForMaker(location.pathname, t);
    }
  };

  // Update menu when user role, language, or merchant status changes
  useEffect(() => {
    const menuConfig = setMenuByUserRole(role);
    setMenu(menuConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, i18n.language, location.pathname, isMerchant]);

  useEffect(() => {
    if (stateConfig.isEndSession) {
      setTitlePopup(`${t("NOTIFICATION")}`);
      setDescriptionPopup(`${t("END_SESSION")}`);
      openNoti();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateConfig]);

  const onCloseModal = () => {
    window.location.href = RoutesString.Login;
  };

  return (
    <>
      <ConfigurationLoader />
      <AppShell
        padding="md"
        navbar={
          sideBar.collapsed ? (
            <Navbar
              height="calc(100% - 16px - 16px)"
              width={{ sm: 300 }}
              style={{
                width: "calc(300px - 16px)",
                minWidth: "calc(300px - 16px)",
                backgroundColor: "white",
                position: "fixed",
                border: "1px solid #E5E7EB",
                borderRadius: "16px",
                margin: 16,
              }}
            >
              <Navbar.Section
                sx={{
                  padding: "20px",
                  borderBottom: "1px dashed #D8DDE2",
                }}
              >
                <Flex align="center" gap={12} justify="space-between">
                  <Box w={120}>
                    <Image src={ImgJavis} w={80} />
                  </Box>
                  <ActionIcon onClick={() => toggleSideBar(!sideBar.collapsed)}>
                    <Image src={IconCollapseLeft} w={24} />
                  </ActionIcon>
                </Flex>
              </Navbar.Section>
              <Navbar.Section
                grow
                style={{
                  padding: "20px 16px",
                  paddingTop: 0,
                }}
                component={ScrollArea}
              >
                <Box
                  style={{
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  }}
                >
                  {links}
                </Box>
              </Navbar.Section>
              <Navbar.Section sx={{ padding: "20px 16px" }}>
                <Button
                  leftIcon={<IconLogout size={24} />}
                  variant="subtle"
                  sx={{
                    "&:hover": { backgroundColor: "transparent" },
                    display: "flex",
                    gap: "12px",
                    color: "#2E3337",
                  }}
                  onClick={() => setOpenLogout(true)}
                >
                  <Text sx={{ fontWeight: 700 }}>{t("LOGOUT")}</Text>
                </Button>
                <ModalSetting
                  opened={openLogout}
                  close={() => setOpenLogout(false)}
                  img={notiLogout}
                  title={t<string>("DO_YOU_WANT_TO_LOG_OUT?")}
                  btn={2}
                />
              </Navbar.Section>
              {/* <ActionIcon
                onClick={() => toggleSideBar(!sideBar.collapsed)}
                sx={{
                  position: "absolute",
                  bottom: "90px",
                  right: "-12px",
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: "#1368BC",
                  border: "1px solid #D8DDE2",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  color: "#fff",
                  zIndex: 10,
                  transition: "background-color 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#0d4f88",
                  },
                }}
              >
                <IconChevronDown
                  size={24}
                  transform="rotate(-90)"
                  color="white"
                  stroke={1}
                />
              </ActionIcon> */}

              {/* <ToggleSidebarButton
                sideBar={sideBar}
                toggleSideBar={toggleSideBar}
              /> */}
            </Navbar>
          ) : (
            <Navbar
              height="100vh"
              width={{ base: 80 }}
              sx={{ position: "fixed" }}
            >
              <Box
                sx={{
                  padding: "16px 0",
                  display: "flex",
                  justifyContent: "center",
                  borderBottom: "1px dashed #D8DDE2",
                }}
              >
                <ActionIcon onClick={() => toggleSideBar(!sideBar.collapsed)}>
                  <Image src={IconCollapseRight} w={24} />
                </ActionIcon>
              </Box>
              <Navbar.Section grow sx={{ padding: "20px 16px" }}>
                <Stack justify="center" spacing={0}>
                  {linksLite}
                </Stack>
              </Navbar.Section>
              <Navbar.Section
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  padding: "24px 0",
                }}
              >
                <ActionIcon
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "48px",
                    height: "48px",
                    color: "#2E3337",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "#2E3337",
                    },
                  }}
                  // onClick={() => actionAuth.logout()}
                  onClick={() => setOpenLogout(true)}
                >
                  <IconLogout size={20} />
                </ActionIcon>
                <ModalSetting
                  opened={openLogout}
                  close={() => setOpenLogout(false)}
                  img={notiLogout}
                  title={t<string>("DO_YOU_WANT_TO_LOG_OUT?")}
                  btn={2}
                />
              </Navbar.Section>
              {/* <ToggleSidebarButton
                sideBar={sideBar}
                toggleSideBar={toggleSideBar}
              /> */}
            </Navbar>
          )
        }
        styles={(theme) => ({
          main: {
            position: "relative",
            paddingLeft: "calc(var(--mantine-navbar-width, 0px))",
            paddingRight: "calc(var(--mantine-aside-width, 0px))",
            paddingBottom: "50px",
            paddingTop: "90px",
            backgroundColor:
              theme.colorScheme === "dark"
                ? theme.colors.dark[8]
                : theme.colors.gray[0],
          },
        })}
      >
        <HeaderBar
          userInfo={user}
          onMenuClick={() => toggleSideBar(!sideBar.collapsed)}
          onUserBlockClick={open}
        />
        <ScrollArea
          type="never"
          // JVC-7521: vùng cuộn thật của trang là viewport của ScrollArea này (không phải window).
          // Gắn id để các màn nhiều bước cuộn về đầu khi chuyển bước (window.scrollTo vô hiệu ở đây).
          viewportProps={{ id: "app-main-scroll" }}
          // Ép vùng nội dung bám theo bề rộng viewport thay vì nở theo content.
          // Mặc định Radix bọc children trong div display:table;min-width:100% nên
          // nội dung rộng (bảng nhiều cột) kéo giãn cả trang. Đổi sang block để nội
          // dung tự co, nhờ đó vùng cuộn ngang riêng bên trong từng bảng mới hoạt
          // động (không phát sinh thanh cuộn ngang thứ hai ở cấp trang).
          styles={{ viewport: { "& > div": { display: "block !important" } } }}
          sx={{ padding: "1.25rem", paddingTop: "0 !important" }}
        >
          {children}
        </ScrollArea>
        {/* <BottomBar /> */}
        {user && (
          <DrawerUserInfo opened={openedDrawer} close={close} userInfo={user} />
        )}
      </AppShell>
      <NotificationPopup
        opened={openedNoti}
        close={closeNoti}
        title={titlePopup}
        description={descriptionPopup}
        closeModal={onCloseModal}
      />
    </>
  );
};

export default LoggedLayout;
